<template>
  <v-layout
    row
    justify-center
  >
    <v-dialog
      v-model="isOpen"
      persistent
      max-width="1000px"
    >
      <v-card class="create-transfer">
        <v-form>
          <v-card-title class="toolbar primary white--text">
            <v-icon dark>
              add_circle
            </v-icon>
            <h3 class="title ml-2">
              Create Transfer
            </h3>
            <v-spacer />
          </v-card-title>
          <v-progress-linear
            v-if="isLoading || isSaving"
            :indeterminate="true"
            class="ma-0"
          />
          <v-card-title
            v-if="!transferResult"
            class="toolbar grey lighten-5 secondary--text"
          />

          <v-card-text>
            <v-container
              class="pa-0"
            >
              <v-layout
                v-if="!transferResult"
                wrap
              >
                <v-layout
                  class="form-group"
                  flex
                  wrap
                >
                  <h6 class="subheading">
                    Transfer details
                  </h6>

                  <v-flex
                    xs12
                    md6
                  >
                    <v-text-field
                      v-model="formData.pieces"
                      class="mr-4"
                      :hint="`${(parseInt(formData.pieces | 0, 0))}`"
                      persistent-hint
                      label="Number of pieces to transfer"
                      type="number"
                      :error-messages="getFieldErrors('pieces')"
                      @blur="$v.formData.pieces.$touch()"
                      @change="$v.formData.pieces.$touch()"
                    />
                  </v-flex>

                  <v-flex
                    xs12
                    md6
                  >
                    <v-text-field
                      v-model="formData.financial_product_id"
                      label="Financial_product_id"
                      hint="fx8en39kyu4fvzs"
                      persistent-hint
                      :error-messages="getFieldErrors('financial_product_id')"
                      @blur="$v.formData.financial_product_id.$touch()"
                      @change="$v.formData.financial_product_id.$touch()"
                    />
                  </v-flex>

                  <v-flex
                    xs12
                    md6
                  >
                    <v-text-field
                      v-model="formData.source_dda_id"
                      label="source_dda_id"
                      :error-messages="getFieldErrors('source_dda_id')"
                      @blur="$v.formData.source_dda_id.$touch()"
                      @change="$v.formData.source_dda_id.$touch()"
                    />
                  </v-flex>

                  <v-flex
                    xs12
                    md6
                  >
                    <v-text-field
                      v-model="formData.target_dda_id"
                      label="target_dda_id"
                      :error-messages="getFieldErrors('target_dda_id')"
                      @blur="$v.formData.target_dda_id.$touch()"
                      @change="$v.formData.target_dda_id.$touch()"
                    />
                  </v-flex>
                </v-layout>
              </v-layout>

              <v-layout
                v-if="!!transferResult"
                wrap
              >
                <v-layout
                  wrap
                  class="form-group"
                >
                  <h6 class="subheading">
                    Transfer result
                  </h6>

                  <v-flex
                    v-for="field in Object.keys(transferResult)"
                    :key="field"
                    xs12
                    md4
                  >
                    <v-text-field
                      v-model="transferResult[field]"
                      readonly
                      class="body-1"
                      :label="formatFieldName(field)"
                    />
                  </v-flex>
                </v-layout>
              </v-layout>
            </v-container>

            <v-alert
              v-if="error"
              :value="error"
              type="error"
              color="error"
            >
              {{ error }}
            </v-alert>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn
              color="secondary"
              flat
              @click="onToggleOpen"
            >
              Close
            </v-btn>

            <v-tooltip
              v-if="!transferResult"
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  :disabled="isLoading || isSaving || !canSubmit"
                  :loading="isLoading || isSaving"
                  :color="!transferResult ? 'primary': 'warning'"
                  @click="submit"
                  v-on="on"
                >
                  <span>Create Transfer</span>
                </v-btn>
              </template>
              <span>Create transfer</span>
            </v-tooltip>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';

export default {
  name: 'CreateTransferDialog',

  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    toggleOpen: {
      type: Function,
      required: true,
    },
  },

  data: () => ({
    isLoading: false,
    error: null,
    formData: {},
  }),

  validations() {
    return {
      formData: {
        pieces: { required }, // "123400"
        financial_product_id: { required }, // "fx8en39kyu4fvzs",
        source_dda_id: { required }, // "138c7960-46de-11ec-9108-bfc0b11be9a4"
        target_dda_id: { required }, // "138c7960-46de-11ec-9108-bfc0b11be932"
      },
    };
  },

  computed: {
    ...mapState({
      transferResult: state => state.eieInvestmentsStore.createdTransfer,
      isSaving: state => state.eieInvestmentsStore.loading,
    }),

    validFields() {
      return Object.keys(this.$v.formData.$params).filter(
        item => !this.$v.formData[item].$invalid,
      );
    },
    errors() {
      return Object.keys(this.$v.formData.$params).filter(
        item => this.$v.formData[item].$invalid,
      );
    },

    canSubmit() {
      if (!this.transferResult) { return true; }

      const { state } = this.transferResult;

      const finalTransferStates = [
        'CreationRejected',
        'CreationFailed',
        'UpdateRejected',
        'UpdateFailed',
        'FinalizationSucceeded',
        'FinalizationRejected',
        'FinalizationFailed',
        'CreationExpired',
      ];

      return !finalTransferStates.includes(state);
    },
  },

  methods: {
    ...mapActions(['createTransfer', 'clearTransferResult']),

    async submit() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.error = null;
        this.isLoading = true;

        try {
          await this.createTransfer(this.formData);
        } catch (e) {
          console.error(JSON.stringify(e, null, 2));
          const serverError = (e.response && e.response.data && e.response.data.error) || e.message || 'An unknown error occurred';
          this.error = `Error creating transfer: ${serverError}`;
        }
        this.isLoading = false;
        this.$forceUpdate();
      }
    },

    getFieldErrors(field) {
      const errors = [];
      if (this.$v.formData[field]) {
        if (!this.$v.formData[field].$dirty || !this.$v.formData[field].$invalid) {
          return errors;
        }

        if (!this.$v.formData[field].required) {
          errors.push('Required field');
        }
      }

      return errors;
    },
    formatFieldName(str, item) {
      const camelCaseSentence = str.replace(/([-_]\w)/g, g => g[1].toUpperCase());

      return _.startCase(camelCaseSentence);
    },

    onToggleOpen() {
      this.toggleOpen();
      this.error = null;
      // this.formData.trancheId = null;
      this.formData = {};
      this.$v.$reset();
      this.clearTransferResult();
    },
  },
};
</script>

<style lang="scss" >
.create-transfer {
  .v-card__title {
    padding: 24px;
  }
  .v-card__text {
    max-height: 65vh;
    overflow: auto;
    padding-top: 32px;
  }
  .v-card__actions {
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }
  .radio-label {
    position: absolute;
    font-size: 16px;
    text-align: left;
    max-width: 133%;
    height: 20px;
    line-height: 20px;
    color: rgba(0, 0, 0, 0.54);
    -webkit-transform: translateY(-18px) scale(0.75);
    transform: translateY(0px) translateX(-6px) scale(0.75);
  }
  .subheading {
    padding: 8px;
    background: white;
    color: rgba(0, 0, 0, 0.54);
    top: -20px;
    left: 8px;
    position: absolute;
    display: flex;
    align-items: center;
  }
  .form-group {
    padding: 16px;
    margin: 0 0 24px 0;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 5px;
    position: relative;
    &.inline {
      display: inline-flex;
      width: 50%;
    }
  }
  .container.grid-list-md .layout:not(:only-child) {
    margin: 0 0 24px 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/eie/investments/dialog/CreateTransferDialog.vue